import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';

import { ALL_STAR_VALUES, REVIEWS_TABLE_DEFAULT_PAGE_SIZE } from 'bundles/reviews/constants';
import courseReviewsPageQuery from 'bundles/reviews/queries/CourseReviewsPage.graphql';
import type {
  CourseReviewsPageDataFragment,
  CourseReviewsPageQuery,
  CourseReviewsPageQueryVariables,
} from 'bundles/reviews/queries/__generated__/CourseReviewsPage';
import type { CourseReviewElement } from 'bundles/unified-description-page-common/hooks/useCourseReviews';
import { useCourseReviews } from 'bundles/unified-description-page-common/hooks/useCourseReviews';

export type CourseReviewsPageData = CourseReviewsPageDataFragment;
export type CourseReviewsPageReview = NonNullable<CourseReviewsPageData['topReviews']>[number];
export type CourseReviewsPageSchemaReview = CourseReviewElement;

type CourseReviewsPageDataResult = {
  loading: boolean;
  error?: Error;
  reviewsPageData: CourseReviewsPageData | null;
  schemaReviews: CourseReviewsPageSchemaReview[];
};

/**
 * Page-level data hook for the standalone course/guided-project reviews page. A single
 * DescriptionPage.queryCourseReviewsPage call covers everything the page renders -- product card,
 * rating breakdown, featured reviews and SEO metadata -- so no per-field fallbacks between sources
 * are needed.
 *
 * `schemaReviews` backs the JSON-LD review list and is deliberately the *same* first page of
 * reviews the table opens with (see REVIEWS_TABLE_DEFAULT_PAGE_SIZE), not `topReviews`: Apollo
 * dedupes the two identical ProductReviewsV1 queries into one request, so the structured data
 * costs no extra round trip.
 */
export function useCourseReviewsPageData(): CourseReviewsPageDataResult {
  const params = useParams();
  const slug = params?.slug ?? '';

  const { data, loading, error } = useQuery<CourseReviewsPageQuery, CourseReviewsPageQueryVariables>(
    courseReviewsPageQuery,
    {
      variables: { slug },
      skip: !slug,
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  const reviewsPageData = data?.DescriptionPage?.queryCourseReviewsPage ?? null;

  // ProductReviewsV1Resource.byProduct's productId is a union field -- courses must be passed as a
  // "COURSE~<id>" stringkey, not the bare course id DescriptionPage returns.
  const { reviews: schemaReviews } = useCourseReviews({
    courseId: reviewsPageData?.id ? `COURSE~${reviewsPageData.id}` : undefined,
    limit: REVIEWS_TABLE_DEFAULT_PAGE_SIZE,
    start: '0',
    ratingValues: ALL_STAR_VALUES,
    sortByHelpfulVotes: true,
  });

  return {
    loading: !!slug && loading,
    error,
    reviewsPageData,
    schemaReviews,
  };
}
